import cs1AssistantConversation from './conversations/cs1';
import cs2AssistantConversation from './conversations/cs2';
import cs3AssistantConversation from './conversations/cs3';
import cs4AssistantConversation from './conversations/cs4';
import cs5AssistantConversation from './conversations/cs5';
import cs6AssistantConversation from './conversations/cs6';
import cs7AssistantConversation from './conversations/cs7';
import cs8AssistantConversation from './conversations/cs8';
import cs9AssistantConversation from './conversations/cs9';
import cs10AssistantConversation from './conversations/cs10';

const caseStudyContents = {
  cs1: {
    simulationMedia: "Simulated daily interaction scenario featuring a multilingual and mood-adaptive virtual assistant.",
    
    userProfile: (
      <div>
        <div><strong>🆔 Subject ID:</strong> 1</div>
        <div><strong>🎂 Age:</strong> 26</div>
        <div><strong>👨 Gender:</strong> Male</div>
        <br />
        <div><strong>💡 Personality at a Glance:</strong>
          <ul className="list-disc list-inside ml-4">
            <li>😊 Moderately outgoing (Extraversion: 5)</li>
            <li>🤝 Warm and cooperative (Agreeableness: 6)</li>
            <li>🗓️ Balanced between spontaneity and organization (Conscientiousness: 5)</li>
            <li>🌤️ Calm under pressure (Emotional Stability: 6)</li>
            <li>🌎 Open-minded (Openness: 5)</li>
          </ul>
        </div>
        <br />
        <div className="mt-2">
          <strong>📝 About Him:</strong> He's a balanced individual who enjoys both socializing and introspective moments. Emotionally resilient and flexible, he enjoys exploring new ideas, often choosing quiet reflection through reading as his go-to relaxation method.
        </div>
      </div>
    ),

    detailedActivities: (
      <div>
        <div><strong>🕰️ Time:</strong> Feb 6, 2025 – 12:30 PM</div>
        <div className="mt-2 whitespace-pre-line">
          <strong>📖 What's Happening:</strong>
          <blockquote className="mt-1 pl-4 border-l-4 border-gray-300 text-gray-700">
            It's midday, quiet and serene. He's comfortably seated on the sofa in the living room, enjoying a moment of peaceful solitude.
            <br /><br />
            With a book in hand, he immerses himself in quiet reflection, savoring this balanced time between his active social life and introspective moments.
            <br /><br />
            The assistant softly adjusts the ambient sound and lighting to complement his calm mood, enhancing his comfortable reading session.
            <br /><br />
            These tranquil moments offer him the perfect retreat, keeping him emotionally balanced and content.
          </blockquote>
        </div>
      </div>
    ),

    conversation: cs1AssistantConversation,

    systemOverview: (
      <div>
        <h4 className="title is-6">Study Overview</h4>
        <p className="mb-4">
          Customization and personalization are pivotal in shaping user experiences with smart home assistants. These devices often become extensions of a user's personality and lifestyle, influencing daily interactions and emotional engagement. However, defining the optimal personality and personalization level for these assistants remains a critical design challenge.
        </p>
        <p className="mb-4">
          To delve deeper into users' preferences for assistant personalities, we replicated the study <em>"I Want It That Way"</em> (Zargham et al., 2022). This original research investigated how users envision their ideal home assistant through storyboard-based interviews, exploring dimensions such as personality traits, interaction styles, and emotional responsiveness. Participants rated their own personality traits and then imagined and evaluated corresponding traits for their ideal home assistants, offering insights into expectations regarding emotional stability, agreeableness, and conscientiousness.
        </p>
        <p className="mb-4">
          The original study highlighted a consistent trend: users often idealize assistants as more emotionally stable, agreeable, and reliable than themselves, suggesting a preference for supportive yet distinctly non-human-like assistants.
        </p>
      </div>
    ),
    gideaReplication: (
      <div>
        <h4 className="title is-6">
          Extended Findings with GIDEA
        </h4>
        <p className="mb-4">
          Using the <strong>GIDEA platform</strong>, we further explored these insights by simulating interactions between avatars and customized home assistants. Each simulated avatar was assigned personality traits based on the TIPI scoring system, mirroring the original study setup. Avatars interacted with personalized assistants in scenarios reflecting everyday tasks and emotional exchanges, subsequently evaluating these interactions through structured surveys.
        </p>

        <div className="flex flex-col md:flex-row gap-6 my-4 w-full justify-center items-center">
          <img src="/summary/cs1.png" alt="Original Study" className="w-full md:w-1/2 max-w-xl rounded shadow" />
          <img src="/summary/cs1_table.jpg" alt="GIDEA Results" className="w-full md:w-1/2 max-w-3xl rounded shadow" />
        </div>

        <p className="mb-4">
          GIDEA simulations consistently aligned with original findings, with avatars rating personalized assistants significantly higher than their self-assessments in traits like emotional stability, agreeableness, and conscientiousness. Simulated users appreciated assistants that were emotionally adaptive and responsive yet clearly distinguished from human interactions, reinforcing the ideal of an emotionally supportive yet distinctly non-human assistant.
        </p>

        <p className="mb-4">
          Additionally, GIDEA avatars mirrored real participants' concerns about potential misinterpretation of emotions by overly human-like assistants, emphasizing a clear preference for interactions framed as supportive tools rather than companions. These findings underscore GIDEA’s effectiveness in replicating nuanced user preferences, highlighting its utility for exploring and refining personalization strategies in smart home assistant design.
        </p>
      </div>
    ),
  },
  cs2: {
      simulationMedia: "What Makes a Good Conversation?: Challenges in Designing Truly Conversational Agents",
      userProfile: (
        <div>
          <div><strong>🆔 Subject ID:</strong> 1</div>
          <div><strong>🎂 Age:</strong> 39</div>
          <div><strong>👩 Gender:</strong> Female</div>
          <div><strong>🖥️ Technology Expertise:</strong> Advanced</div>
          <br></br>
          <div><strong>💡 Personality at a Glance:</strong>
            <ul className="list-disc list-inside ml-4">
              <li>🌙 Very introverted (Extraversion: 1)</li>
              <li>🤝 Moderately cooperative (Agreeableness: 4)</li>
              <li>📅 Moderately organized (Conscientiousness: 4)</li>
              <li>🌊 Highly sensitive (Emotional Stability: 1)</li>
              <li>🧠 Moderately open-minded (Openness: 4)</li>
            </ul>
          </div>
          <br></br>
          <div className="mt-2">
            <strong>📝 About Her:</strong> Sarah is a 39-year-old woman with advanced technology expertise and near-native language proficiency. She frequently uses Alexa to help manage her busy days. Sarah prefers an organized approach and values reliability, evident in her moderate agreeableness and conscientiousness. While she enjoys solitary activities due to her low extraversion, she is open to new experiences and ideas. Her emotional side sometimes leads her to be sensitive, yet this trait also fuels her creativity and adaptive thinking.
          </div>
        </div>
      ),
      detailedActivities: (
      <div>
        <div><strong>🕰️ Time:</strong> Feb 6, 2025 – 7:00 AM</div>
        <div className="mt-2 whitespace-pre-line">
          <strong>📖 What's Happening:</strong>
          <blockquote className="mt-1 pl-4 border-l-4 border-gray-300 text-gray-700">
            Sarah enjoys starting her day with structure and organization. Given her frequent use of technology and the need for reliable scheduling, she begins her morning by checking her emails quietly in her home office. This reflects her conscientious nature and the way she prefers to handle daily responsibilities.

            Sitting at her desk, Sarah methodically opens her laptop and logs into her email account, immediately categorizing each message by importance. She swiftly handles urgent emails, marking less pressing matters to be addressed later. Her focused demeanor and efficient workflow underscore her moderate conscientiousness and advanced technical expertise.

            The quiet ambiance of her home office enhances her concentration, allowing Sarah to plan her day effectively. The neatly organized space mirrors her appreciation for order, providing an optimal environment for productivity. This morning routine is essential for Sarah, allowing her to maintain control over her busy schedule and ensuring she is prepared for upcoming commitments.
          </blockquote>
        </div>
      </div>
    ),
    conversation: cs2AssistantConversation,
    systemOverview: (
      <div>
        <h4 className="title is-6">Study Overview</h4>
        <p className="mb-4">
          The study, titled <em>"What Makes a Good Conversation?: Challenges in Designing Truly Conversational Agents,"</em> investigates critical aspects of conversation quality by comparing human-human and human-agent interactions. It specifically examines essential conversational features such as emotional connection, trust-building, humor, mutual understanding, and personalization.
        </p>
        <p className="mb-4">
          Participants discussed various conversational contexts, describing the characteristics they value in both social (emotionally driven, supportive, personal) and transactional (goal-oriented, efficient, clear) interactions. Emphasis was placed on the role of empathy, trust, humor, and the challenges agents face in mimicking the nuances inherent in human conversations. Insights gathered underscore users’ expectations of conversational agents in effectively bridging the gap between functional reliability and emotional depth.
        </p>
        <p className="mb-4">
          Key highlights of the study include the identification of humor and empathy as essential components that enhance interactional quality. Additionally, the research emphasizes the need for careful personalization, balancing customization with privacy concerns, crucial for establishing trust in human-agent interactions.
        </p>
      </div>
    ),
    gideaReplication: (
      <div>
        <h4 className="title is-6">
          Extended Findings with GIDEA
        </h4>
        <p className="mb-4">
          Leveraging the GIDEA platform, this study's nuanced conversational dynamics were explored in simulated interactions, replicating and extending original findings. The avatars interacted extensively with conversational agents, highlighting significant themes from the original research, including emotional grounding, empathy, humor management, and personalized responsiveness.
        </p>
        <div className="flex flex-col md:flex-row gap-6 my-4 w-full justify-center items-center">
          <img src="/summary/cs2_table.jpg" alt="GIDEA Results" className="w-full md:w-1/2 max-w-3xl rounded shadow" />
        </div>
        <p className="mb-4">
          The simulations demonstrated the critical importance of empathy and emotional intelligence in agent communication. Avatars expressed clear appreciation for conversational agents capable of recognizing and appropriately responding to emotional cues, suggesting supportive messages during stressful or emotionally charged interactions.
        </p>
        <p className="mb-4">
          Humor emerged as another crucial interactional element, requiring careful calibration to maintain conversational appropriateness without undermining task efficiency. Avatars valued humor in casual contexts, reinforcing the original findings on context sensitivity and tonal appropriateness.
        </p>
        <p className="mb-4">
          Personalization was confirmed as vital, with avatars stressing the need for agents to respect boundaries and privacy. Transparency regarding data use and user-controlled privacy settings were frequently emphasized, highlighting the delicate balance between effective personalization and user privacy.
        </p>
        <p className="mb-4">
          Overall, GIDEA's simulations effectively replicated and enriched the study’s original insights, affirming the central role of empathy, personalized interaction, and appropriate humor in designing genuinely conversational and trustworthy agents.
        </p>
      </div>
    ),
    },
  cs3: {
    simulationMedia: "Simulation/Media content for CS3 (placeholder)",
    userProfile: (
      <div>
        <div><strong>🆔 Subject ID:</strong> 1</div>
        <div><strong>🎂 Age:</strong> 50</div>
        <div><strong>👩 Gender:</strong> Female</div>
        <div><strong>🏫 Occupation:</strong> Teacher / Library Staff</div>
        <br></br>
        <div><strong>💡 Personality at a Glance:</strong>
          <ul className="list-disc list-inside ml-4">
            <li>😄 Super outgoing (Extraversion: 7)</li>
            <li>🤝 Very kind and warm (Agreeableness: 6)</li>
            <li>🎨 A little spontaneous (Conscientiousness: 3)</li>
            <li>🌤️ Calm under pressure (Emotional Stability: 7)</li>
            <li>📚 Prefers the familiar (Openness: 3)</li>
          </ul>
        </div>
        <br></br>
        <div className="mt-2">
          <strong>📝 About Her:</strong> Female, age 50, working in education, extremely high in extraversion, quite high in agreeableness, moderate in conscientiousness. Has some experience with voice assistants and shows a preference for informal interaction.
        </div>
      </div>
    ),    
    detailedActivities: (
      <div>
        <div><strong>🕰️ Time:</strong> Oct 12, 2023 – 12:30 PM</div>
        <div className="mt-2 whitespace-pre-line">
          <strong>📖 What's Happening:</strong>
          <blockquote className="mt-1 pl-4 border-l-4 border-gray-300 text-gray-700">
            It's an afternoon at home. She is sitting on the sofa with a pillow behind her, reading a book she's been meaning to start. The room is quiet, giving her a moment of peace after a busy morning.

            At 12:45 PM, while she is focused on her reading, the smart assistant automatically dims the lights slightly to adjust to the changing daylight. She notices this and appreciates the convenience. She adjusts her glasses, wraps herself more comfortably in a blanket, and continues reading, enjoying this quiet and relaxing time before returning to her busy teaching schedule.
          </blockquote>
        </div>
      </div>
    ),    
    conversation: cs3AssistantConversation,
    systemOverview: (
      <div>
        <h4 className="title is-6">Study Overview</h4>
        <p className="mb-4">
          Smart assistants have the potential to significantly support older adults by facilitating routine tasks, offering emotional support, and enhancing overall quality of life. However, designing effective voice assistants (VAs) for this demographic requires understanding the nuanced preferences, habits, and expectations shaped by age and lifestyle.
        </p>
        <p className="mb-4">
          To examine how voice assistant design influences user perception among older adults, we replicated the study <em>"Like My Aunt Dorothy"</em> (Chin et al., 2024). The original research specifically compared how <strong>formal vs. informal speech styles</strong> impacted perceptions of voice assistants among <strong>middle-aged adults (50–64)</strong> and <strong>older adults (65–80)</strong>. Participants in the original study interacted with RAVA, a simulated voice assistant, in a smart home environment, engaging in cognitively demanding tasks designed to reveal user attitudes towards conversational styles. Key measures included perceived intelligence, trustworthiness, likability, and metaphorical framing, providing valuable insights into user-assistant relationships.
        </p>
        <p className="mb-4">
          By capturing variations in users' reactions and metaphorical descriptions — ranging from professional to familial comparisons — the original study shed light on the complex emotional and cognitive factors influencing acceptance and engagement with voice assistants.
        </p>
      </div>
    ),
    gideaReplication: (
      <div>
        <h4 className="title is-6">
          Extended Findings with GIDEA
        </h4>
        <p className="mb-4">
          Leveraging the <strong>GIDEA platform</strong>, we expanded upon the original research by simulating similar interactions using avatars powered by large language models (LLMs). Each simulated avatar participated in a <strong>Pattern Recall Game</strong>, interacting with a VA that adopted either a formal or informal conversational style. Following these interactions, simulated avatars completed post-task surveys, mirroring evaluation methods from the original study.
        </p>

        <div className="flex flex-col md:flex-row gap-6 my-4 w-full justify-center items-center">
          <img src="/summary/cs3.png" alt="Original Study" className="w-full md:w-1/2 max-w-xl rounded shadow" />
          <img src="/summary/cs3_table.jpg" alt="GIDEA Results" className="w-full md:w-1/2 max-w-3xl rounded shadow" />
        </div>

        <p className="mb-4">
          The GIDEA simulations closely reproduced key findings of the original study, notably capturing distinct differences in user perception influenced by conversational style. Formal assistants were reliably perceived as more structured, intelligent, and trustworthy, often prompting users to describe them using professional metaphors such as "teacher," "instructor," or "guidebook." Conversely, informal assistants elicited emotional warmth and were frequently characterized as "friendly," "supportive," or akin to a "cheerleader" or "aunt."
        </p>

        <p className="mb-4">
          Despite these alignments, GIDEA-generated responses displayed less metaphorical diversity compared to human participants, often reusing familiar terms rather than spontaneously generating novel metaphors. Nonetheless, this consistency validated GIDEA’s capacity to replicate nuanced user reactions, reinforcing the platform's value for early-stage exploration of design concepts aimed at older adult populations.
        </p>
      </div>
    ),    
  },
  cs4: {
      simulationMedia: "Simulation/Media content for CS4 (placeholder)",
      userProfile: (
        <div>
          <div><strong>🆔 Subject ID:</strong> 1</div>
          <div><strong>🎂 Age:</strong> 21</div>
          <div><strong>👩 Gender:</strong> Female</div>
          <div><strong>🔊 Voice Assistant Usage:</strong> Frequent</div>
          <br></br>
          <div><strong>💡 Personality at a Glance:</strong>
            <ul className="list-disc list-inside ml-4">
              <li>🌙 Very introverted (Extraversion: 2)</li>
              <li>🤝 Highly agreeable (Agreeableness: 6)</li>
              <li>📅 Very spontaneous (Conscientiousness: 1)</li>
              <li>🌤️ Calm and stable (Emotional Stability: 6)</li>
              <li>🧠 Moderately open to new ideas (Openness: 4)</li>
            </ul>
          </div>
          <br></br>
          <div className="mt-2">
            <strong>📝 About Her:</strong> Subject 1 is a 21-year-old woman who frequently uses voice assistants to organize her daily tasks. Despite her very low levels of extraversion, she enjoys deep connections with close friends, highlighting her agreeable nature. Her spontaneous character often results in a flexible daily routine. With her calm demeanor, she approaches challenges rationally, leveraging her moderate openness and creativity to solve problems.
          </div>
        </div>
      ),
      detailedActivities: (
        <div>
          <div><strong>🕰️ Time:</strong> Oct 11, 2023 – 7:00 AM</div>
          <div className="mt-2 whitespace-pre-line">
            <strong>📖 What's Happening:</strong>
            <blockquote className="mt-1 pl-4 border-l-4 border-gray-300 text-gray-700">
              The gentle hum of the ceiling fan fills the bedroom as early morning light filters through the curtains. Subject 1 wakes gently, casually activating her smart speaker to hear the weather and daily schedule. The structured briefing, listing the day's university lecture, a book deadline, and a lunch with a friend, offers her a comforting balance of routine and flexibility. She plans to postpone the book task and eagerly anticipates social interaction over lunch.

              After the briefing, she feels organized and ready, moving comfortably around her room, tidying up absent-mindedly. She proceeds to the kitchen, initiating her coffee-making routine casually, improvising with a touch of cinnamon for a new flavor experience. While brewing, she absorbs the cozy ambiance created by automated lighting adjustments, finding joy and calm in these simple routines.
            </blockquote>
          </div>
        </div>
      ),
      conversation: cs4AssistantConversation,
      systemOverview: (
      <div>
        <h4 className="title is-6">Study Overview</h4>
        <p className="mb-4">
          This study explores how <strong>proactive feedback</strong> from conversational assistants impacts user decision-making and reflection during <strong>meal selections</strong>. Specifically, it examines the differences in user experiences when receiving <strong>solicited versus unsolicited nutritional advice</strong>. Users' perceptions of interaction quality, feedback relevance, and trust towards the conversational agent are evaluated, emphasizing factors such as <strong>clarity, timing, and feedback delivery style</strong>.
        </p>
        <p className="mb-4">
          Participants navigated meal choices through structured interactions, choosing starters, main courses, and desserts. They were provided with the option to request nutritional feedback, allowing researchers to observe their <strong>preferences regarding feedback solicitation</strong>. The study identifies critical aspects that influence user comfort, such as autonomy in soliciting advice, neutrality and clarity in delivery, and the overall impact on decision-making confidence.
        </p>
        <p className="mb-4">
          Findings underline the importance of <strong>user autonomy</strong> and clearly structured proactive feedback to enhance decision confidence and satisfaction without overwhelming or unnecessary persuasion.
        </p>
      </div>
    ),
    gideaReplication: (
      <div>
        <h4 className="title is-6">
          Extended Findings with GIDEA
        </h4>
        <p className="mb-4">
          The <strong>GIDEA platform</strong> simulations effectively extended the original research by replicating key interaction dynamics and decision-making processes. Simulated avatars demonstrated a clear preference for <strong>solicited feedback</strong>, which significantly enhanced their comfort and decision-making confidence. Users particularly valued the clarity, specificity, and relevance of feedback provided by the conversational assistant, appreciating its <strong>neutral tone</strong> that maintained an informative approach without undue influence.
        </p>
        <div className="flex flex-col md:flex-row gap-6 my-4 w-full justify-center items-center">
          <img src="/summary/cs4.png" alt="Original Study" className="w-full md:w-1/2 max-w-xl rounded shadow" />
          <img src="/summary/cs4_table.jpg" alt="GIDEA Results" className="w-full md:w-1/2 max-w-3xl rounded shadow" />
        </div>
        <p className="mb-4">
          The simulations revealed how users valued <strong>structured guidance</strong>, especially when indecisive, and viewed unsolicited feedback as intrusive. Emphasizing autonomy, avatars consistently responded positively when given explicit control over the feedback process, reflecting preferences identified in the original research.
        </p>
        <p className="mb-4">
          Additional insights from the simulation suggested the potential to further enhance user experience through increased <strong>personalization</strong> and subtle <strong>enthusiasm</strong> in feedback delivery. Participants recommended developing conversational agents that offer personalized responses based on past interactions, thus boosting engagement without compromising autonomy.
        </p>
        <p className="mb-4">
          Overall, the GIDEA simulations reinforced original findings and offered nuanced insights into the delicate balance of providing proactive, yet non-intrusive, conversational feedback.
        </p>
      </div>
    ),
  },
  cs5: {
    simulationMedia: "Simulation/Media content for CS5 (placeholder)",
    userProfile: (
      <div>
        <div><strong>🆔 Subject ID:</strong> 1</div>
        <div><strong>🎂 Age:</strong> 25</div>
        <div><strong>👨 Gender:</strong> Male</div>
        <div><strong>🏠 Living Arrangement:</strong> Lives alone in a cozy 40 m² apartment</div>
        <br></br>
        <div><strong>💡 Personality at a Glance:</strong>
          <ul className="list-disc list-inside ml-4">
            <li>😌 Moderately extraverted (Extraversion: 4)</li>
            <li>🗣️ Very direct, low agreeableness (Agreeableness: 1)</li>
            <li>📅 Moderately conscientious (Conscientiousness: 4)</li>
            <li>🌊 Very emotionally stable (Emotional Stability: 7)</li>
            <li>🌍 Open-minded and curious (Openness: 6)</li>
          </ul>
        </div>
        <br></br>
        <div className="mt-2">
          <strong>📝 About Him:</strong> Subject 1 is a 25-year-old male who enjoys the independence of living alone. He values minimalist design and has a keen interest in technology and modern art. Preferring solitude, he enjoys intellectual and creative projects, often exploring new ideas through documentaries and books. He maintains a straightforward and direct communication style, favoring meaningful engagements and disliking crowded social events.
        </div>
      </div>
    ),
    detailedActivities: (
      <div>
        <div><strong>🕰️ Time:</strong> Feb 6, 2025 – 12:30 PM</div>
        <div className="mt-2 whitespace-pre-line">
          <strong>📖 What's Happening:</strong>
          <blockquote className="mt-1 pl-4 border-l-4 border-gray-300 text-gray-700">
            As the clock strikes 12:30 pm, Subject 1 moves from the kitchen to the living room, transitioning smoothly from breakfast to leisure. He settles into his minimalist sofa, prepared to indulge in a documentary that piques his curiosity about modern art. The soft glow of the room, complemented by automated lighting, offers an ideal environment for intellectual stimulation. His fingers effortlessly activate the television, and he immerses himself deeply in the narrative, embracing the tranquility and intellectual engagement the documentary provides.

            Later, feeling inspired, he selects a book from his neatly organized shelf and continues this quiet, contemplative pursuit. The harmonious blend of comfort, tranquility, and intellectual curiosity defines his early afternoon routine, allowing his mind to explore new insights without distraction.

            Eventually feeling hunger stir, he moves to the kitchen, preparing a simple and nutritious meal, cherishing the peaceful solitude and minimalist ambiance. Each action—from preparation to tidying afterward—is carried out with mindful appreciation, maintaining the tranquility and clarity he values.
          </blockquote>
        </div>
      </div>
    ),
    conversation: cs5AssistantConversation,
    systemOverview: (
      <div>
        <h4 className="title is-6">Study Overview</h4>
        <p className="mb-4">
          The study titled <em>"Understanding User Perceptions of Proactive Smart Speakers"</em> examines how users perceive and accept <strong>proactive behaviors</strong> from smart assistants. Key areas of focus include user <strong>comfort</strong>, <strong>autonomy</strong>, and <strong>context-awareness</strong> in interactions, aiming to identify the optimal conditions under which proactive prompts enhance rather than disrupt user experiences.
        </p>
        <p className="mb-4">
          Highlighted findings from the original study emphasize the critical role of <strong>timing</strong>, <strong>relevance</strong>, and <strong>control</strong> in shaping user acceptance. Users generally respond positively to proactive interactions when these suggestions are <strong>contextually appropriate</strong>, <strong>timely</strong>, and respectful of their current activities and privacy. Conversely, <strong>intrusive or poorly timed prompts</strong> can significantly diminish user satisfaction.
        </p>
        <p className="mb-4">
          Additionally, the original study explored <strong>social presence and co-activity</strong>, illustrating how proactive suggestions that align with ongoing tasks or user moods can positively influence perceptions of <strong>trust and comfort</strong>, fostering a more engaging and helpful user experience.
        </p>
      </div>
    ),
    gideaReplication: (
      <div>
        <h4 className="title is-6">
          Extended Findings with GIDEA
        </h4>
        <p className="mb-4">
          Utilizing the GIDEA platform, simulations effectively replicated and expanded upon the findings of the original research. Avatars consistently preferred proactive interactions that were carefully <strong>timed</strong>, <strong>context-sensitive</strong>, and respectful of their <strong>autonomy</strong>. They particularly valued interactions that seamlessly integrated into their current activities and emotional states.
        </p>
        <p className="mb-4">
          Notable highlights from GIDEA's simulations include positive reactions to proactive behaviors that offered <strong>optional assistance</strong>, giving users explicit <strong>control over accepting or declining suggestions</strong>. Such interactions significantly enhanced <strong>user satisfaction and perceived utility</strong>.
        </p>
        <div className="flex flex-col md:flex-row gap-6 my-4 w-full justify-center items-center">
          <img src="/summary/cs5.png" alt="Original Study" className="w-full md:w-1/2 max-w-xl rounded shadow" />
          <img src="/summary/cs5_table.jpg" alt="GIDEA Results" className="w-full md:w-1/2 max-w-3xl rounded shadow" />
        </div>
        <p className="mb-4">
          Avatars also appreciated proactive interventions tailored to their <strong>preferences and routines</strong>, such as personalized content recommendations and task-specific prompts that reflected their known interests and ongoing engagements. Conversely, avatars showed clear dissatisfaction when proactive suggestions were perceived as <strong>intrusive or irrelevant</strong>.
        </p>
        <p className="mb-4">
          Overall, the GIDEA platform reinforced the importance of nuanced proactive behavior design, underscoring <strong>timing precision</strong>, <strong>user control</strong>, <strong>contextual relevance</strong>, and <strong>respect for privacy</strong> as vital factors in enhancing user acceptance and satisfaction with smart speaker interactions.
        </p>
      </div>
    ),
  },
  cs6: {
    simulationMedia: "Simulation/Media content for CS6 (placeholder)",
    userProfile: (
      <div>
        <div><strong>🆔 Subject ID:</strong> 1</div>
        <div><strong>🎂 Age:</strong> 33</div>
        <div><strong>👨 Gender:</strong> Male</div>
        <div><strong>🎓 Education:</strong> Bachelor's degree</div>
        <div><strong>📱 Technology Usage:</strong> Rarely engages with smart speakers, prefers analog activities</div>
        <br></br>
        <div><strong>💡 Personality at a Glance:</strong>
          <ul className="list-disc list-inside ml-4">
            <li>🌙 Very introverted (Extraversion: 1)</li>
            <li>🤝 Moderately cooperative (Agreeableness: 4)</li>
            <li>📅 Highly conscientious, values structure (Conscientiousness: 6)</li>
            <li>🌊 Emotionally balanced (Emotional Stability: 5)</li>
            <li>🔒 Prefers routine and familiarity, low openness (Openness: 1)</li>
          </ul>
        </div>
        <br></br>
        <div className="mt-2">
          <strong>📝 About Him:</strong> John is a 33-year-old male with a bachelor's degree who thrives on structure and routine. He rarely uses smart speakers, preferring traditional activities like reading and solitary sports. He communicates clearly and factually, avoiding unnecessary small talk. While reserved, John enjoys quality time with close friends or family and dislikes unpredictability and crowded social events.
        </div>
      </div>
    ),
    detailedActivities: (
      <div>
        <div><strong>🕰️ Time:</strong> Feb 6, 2025 – 12:30 PM to 1:00 PM</div>
        <div className="mt-2 whitespace-pre-line">
          <strong>📖 What's Happening:</strong>
          <blockquote className="mt-1 pl-4 border-l-4 border-gray-300 text-gray-700">
            At 12:30 pm, John decides to read, seeking a structured break from his morning activities. He deliberately selects a familiar book, appreciating the quiet, orderly environment of his home. The activity aligns perfectly with his preference for routine and structured personal time, offering him comfort and intellectual engagement without the need for digital interaction.
          </blockquote>
        </div>
      </div>
    ),
    conversation: cs6AssistantConversation,
    systemOverview: (
      <div>
        <h4 className="title is-6">Study Overview</h4>
        <p className="mb-4">
          The study titled <em>"Understanding Circumstances for Desirable Proactive Behaviour of Voice Assistants: The Proactivity Dilemma"</em> explores user preferences towards proactive interventions by voice assistants. It specifically investigates user attitudes regarding the usefulness, appropriateness, and invasiveness of proactive behaviors across various scenarios, including reminders, health alerts, technical support, and social interactions.
        </p>
        <p className="mb-4">
          Key findings emphasize the crucial role of <strong>context sensitivity</strong>, highlighting user appreciation for proactive interventions perceived as beneficial, urgent, or supportive, such as emergency alerts and health warnings. Conversely, interventions perceived as intrusive or poorly timed were viewed negatively, emphasizing the importance of careful consideration of <strong>timing</strong> and <strong>user autonomy</strong>.
        </p>
        <p className="mb-4">
          The study underscores the need for proactive voice assistants to balance their interventions, ensuring they are both helpful and respectful of user boundaries, preferences, and routines.
        </p>
      </div>
    ),
    gideaReplication: (
      <div>
        <h4 className="title is-6">
          Extended Findings with GIDEA
        </h4>
        <p className="mb-4">
          Utilizing the GIDEA platform, simulations closely replicated and extended the original findings of the study. The avatars demonstrated clear preferences for proactive behaviors that were <strong>contextually appropriate</strong>, particularly valuing scenarios involving urgent assistance and critical reminders.
        </p>
        <div className="flex flex-col md:flex-row gap-6 my-4 w-full justify-center items-center">
          <img src="/summary/cs6.png" alt="Original Study" className="w-full md:w-1/2 max-w-xl rounded shadow" />
          <img src="/summary/cs6_table.jpg" alt="GIDEA Results" className="w-full md:w-1/2 max-w-3xl rounded shadow" />
        </div>
        <p className="mb-4">
          Avatars showed positive responses to interventions that provided practical value without disrupting their established routines, reinforcing the importance of <strong>context sensitivity and timely intervention</strong>. Interactions perceived as supportive yet unobtrusive, such as gentle reminders and neutral mediation in social scenarios, were especially appreciated.
        </p>
        <p className="mb-4">
          In contrast, the simulations confirmed user dissatisfaction with poorly timed or overly intrusive interventions, highlighting the significance of maintaining user autonomy. Avatars clearly indicated the need for proactive assistants to better understand and adapt to user preferences and emotional contexts to avoid frustration.
        </p>
        <p className="mb-4">
          Overall, GIDEA's simulations reinforce critical insights into the design of proactive assistants, emphasizing the importance of balancing <strong>user control, timing, and context-aware interventions</strong> to enhance acceptance and satisfaction.
        </p>
      </div>
    ),
  },
  cs7: {
    simulationMedia: "Simulation/Media content for CS7 (placeholder)",
    userProfile: (
      <div>
        <div><strong>🆔 Subject ID:</strong> 1</div>
        <div><strong>🎂 Age:</strong> 33</div>
        <div><strong>👨 Gender:</strong> Male</div>
        <div><strong>🎓 Education:</strong> PhD degree</div>
        <div><strong>📱 Technology Usage:</strong> Never used a smart speaker, prefers analog and outdoor activities</div>
        <br></br>
        <div><strong>💡 Personality at a Glance:</strong>
          <ul className="list-disc list-inside ml-4">
            <li>🌞 Highly extraverted (Extraversion: 6)</li>
            <li>🤝 Moderately cooperative (Agreeableness: 4)</li>
            <li>📅 Moderately conscientious (Conscientiousness: 4)</li>
            <li>🌊 Emotionally balanced (Emotional Stability: 5)</li>
            <li>🔍 Low openness, values routine and familiarity (Openness: 3)</li>
          </ul>
        </div>
        <br></br>
        <div className="mt-2">
          <strong>📝 About Him:</strong> Subject 1 is a 33-year-old male holding a PhD who greatly values routine and intellectual activities. Although highly extraverted, he has never used a smart speaker and prefers traditional or outdoor activities. He is outgoing, approachable, and enjoys intellectual discussions, academic pursuits, and outdoor recreation, yet he prefers quieter settings to noisy or crowded environments.
        </div>
      </div>
    ),
    detailedActivities: (
      <div>
        <div><strong>🕰️ Time:</strong> Oct 20, 2023 – 7:10 AM to 7:20 AM</div>
        <div className="mt-2 whitespace-pre-line">
          <strong>📖 What's Happening:</strong>
          <blockquote className="mt-1 pl-4 border-l-4 border-gray-300 text-gray-700">
            At 7:10 AM, he quietly enters the restroom for his morning hygiene routine. He carefully brushes his teeth, mindful not to disturb others in the dormitory. Reflecting briefly on his day's plans, he enjoys the quiet routine, appreciating the calm start to the morning. After rinsing and drying, he neatly places everything back and quietly exits, prepared to move seamlessly into his next activity.
          </blockquote>
        </div>
      </div>
    ),
    conversation: cs7AssistantConversation,
    systemOverview: (
      <div>
        <h4 className="title is-6">Study Overview</h4>
        <p className="mb-4">
          The study titled <em>"Hello There! Is Now a Good Time to Talk?: Opportune Moments for Proactive Interactions with Smart Speakers"</em> explores the dynamics of user receptivity to proactive interruptions by smart assistants. Specifically, it focuses on identifying <strong>optimal timing</strong> and <strong>contextual factors</strong> that influence user comfort and acceptance of proactive interactions.
        </p>
        <p className="mb-4">
          Key findings from the original research emphasize the importance of recognizing user <strong>context and activity type</strong>. Users prefer interruptions during transition periods between tasks, or when engaged in low-focus activities, such as leisure or routine tasks. In contrast, interruptions during high-focus activities, like studying or working, were seen as <strong>intrusive and disruptive</strong>.
        </p>
        <p className="mb-4">
          Additionally, the study highlights the need for proactive systems to balance the timing and nature of their interactions carefully, ensuring interventions are perceived as <strong>useful, contextually appropriate</strong>, and respectful of user autonomy.
        </p>
      </div>
    ),
    gideaReplication: (
      <div>
        <h4 className="title is-6">
          Extended Findings with GIDEA
        </h4>
        <p className="mb-4">
          Using the GIDEA platform, the simulations successfully replicated and expanded upon the original study’s insights into proactive interaction timing. Avatars demonstrated strong preferences for interruptions occurring during <strong>natural transition points</strong> between tasks, reflecting increased openness to engagement during these times.
        </p>
        <div className="flex flex-col md:flex-row gap-6 my-4 w-full justify-center items-center">
          <img src="/summary/cs7.png" alt="Original Study" className="w-full md:w-1/3 max-w-lg rounded shadow" />
          <img src="/summary/cs7_table.jpg" alt="GIDEA Results" className="w-full md:w-2/3 max-w-2xl rounded shadow" />
        </div>
        <p className="mb-4">
          Avatars consistently rated interruptions negatively during periods of focused activity, such as intensive work or study sessions, underscoring the need for assistants to exhibit enhanced <strong>context-awareness and sensitivity to cognitive load</strong>. Interruptions during routine or low-focus activities, like brushing teeth or preparing coffee, were viewed more favorably when introduced at the precise moment activities concluded.
        </p>
        <p className="mb-4">
          The simulation reinforced users' preferences for maintaining <strong>control and autonomy</strong>, with avatars particularly valuing interventions that respected their established routines and allowed for smooth transitions between tasks. Avatars clearly indicated the importance of non-intrusive, context-sensitive communication to enhance the acceptance and utility of proactive interactions.
        </p>
        <p className="mb-4">
          Overall, the GIDEA platform's findings emphasize that successful proactive voice assistant interventions depend significantly on precise <strong>timing, contextual appropriateness, and respect for user autonomy and routine</strong>.
        </p>
      </div>
    ),
  },
  cs8: {
    simulationMedia: "Simulation/Media content for CS8 (placeholder)",
    userProfile: (
      <div>
        <div><strong>🆔 Subject ID:</strong> 1</div>
        <div><strong>🎂 Age:</strong> 18</div>
        <div><strong>👨 Gender:</strong> Male</div>
        <div><strong>🎓 Education:</strong> Currently studying, highly disciplined and organized</div>
        <div><strong>📱 Technology Usage:</strong> Frequently engages with structured activities, often involving technology, but does not own a smart speaker</div>
        <br></br>
        <div><strong>💡 Personality at a Glance:</strong>
          <ul className="list-disc list-inside ml-4">
            <li>😐 Moderately extraverted (Extraversion: 4)</li>
            <li>🗣️ Very direct, low agreeableness (Agreeableness: 1)</li>
            <li>📅 Highly conscientious, meticulous and organized (Conscientiousness: 7)</li>
            <li>🌊 Emotionally balanced (Emotional Stability: 5)</li>
            <li>🔒 Prefers structured environments, moderate openness (Openness: 3)</li>
          </ul>
        </div>
        <br></br>
        <div className="mt-2">
          <strong>📝 About Him:</strong> Max is an 18-year-old highly disciplined individual who prefers working quietly on his own structured projects rather than engaging in group activities. Meticulously organized, he enjoys creating detailed plans for studies and hobbies, such as coding or solo sports. Max values direct communication and avoids small talk, maintaining a calm demeanor even under stress. While interested in new technologies, he doesn't own a smart speaker and prefers deep, structured interactions to casual social engagements.
        </div>
      </div>
    ),
    detailedActivities: (
      <div>
        <div><strong>🕰️ Time:</strong> Oct 6, 2023 – 12:30 PM to 1:30 PM</div>
        <div className="mt-2 whitespace-pre-line">
          <strong>📖 What's Happening:</strong>
          <blockquote className="mt-1 pl-4 border-l-4 border-gray-300 text-gray-700">
            At 12:30 pm, Max transitions into working on his coding project. Choosing a quiet, focused environment, he dedicates himself fully to the meticulous and structured task. His concentration is high as he organizes his workspace carefully, laying out coding notes and adjusting his computer settings for optimal productivity. This focused session provides him the structured, solitary environment he prefers, allowing him to make significant progress without distractions.
          </blockquote>
        </div>
      </div>
    ),
    conversation: cs8AssistantConversation,
    systemOverview: (
      <div>
        <h4 className="title is-6">Study Overview</h4>
        <p className="mb-4">
          The study titled <em>"May I Interrupt? Diverging Opinions on Proactive Smart Speakers"</em> examines user attitudes toward various proactive interventions by smart assistants. Specifically, it explores the complex balance between <strong>helpfulness</strong>, <strong>appropriateness</strong>, <strong>pleasantness</strong>, and potential <strong>invasiveness</strong> of smart speaker interactions across different scenarios, including reminders, health alerts, technical assistance, and social contexts.
        </p>
        <p className="mb-4">
          Original findings highlight the nuanced responses users have towards proactive assistance. While users highly value timely and context-appropriate interventions in urgent scenarios (like emergency alerts and critical reminders), they find interactions that disrupt leisure or social engagements (such as spoilers in quizzes or intrusive fact-checking) significantly less desirable.
        </p>
        <p className="mb-4">
          The study emphasizes the critical importance of understanding <strong>context</strong>, <strong>user autonomy</strong>, and the delicate balance required to provide proactive assistance without compromising user satisfaction and comfort.
        </p>
      </div>
    ),
    gideaReplication: (
      <div>
        <h4 className="title is-6">
          Extended Findings with GIDEA
        </h4>
        <p className="mb-4">
          Through simulations using the GIDEA platform, the study's original findings were reinforced and further expanded upon. Avatars demonstrated clear preferences for proactive interactions that were perceived as highly <strong>useful</strong> and <strong>contextually appropriate</strong>, particularly valuing critical reminders, urgent alerts, and helpful technical support.
        </p>
        <div className="flex flex-col md:flex-row gap-6 my-4 w-full justify-center items-center">
          <img src="/summary/cs8.png" alt="Original Study Illustration" className="w-full md:w-1/3 max-w-lg rounded shadow" />
          <img src="/summary/cs8_table.jpg" alt="GIDEA Replication Table" className="w-full md:w-2/3 max-w-2xl rounded shadow" />
        </div>
        <p className="mb-4">
          Avatars positively rated proactive interventions when delivered gently and respectfully, highlighting the importance of maintaining <strong>user autonomy</strong> and control over interactions. They expressed significant dissatisfaction with poorly timed or intrusive interventions, especially during leisure or social activities, underscoring the importance of <strong>context sensitivity and timing precision</strong>.
        </p>
        <p className="mb-4">
          The simulations emphasized the importance of customizing the frequency and manner of proactive interactions to suit individual user preferences, reducing the risk of invasiveness and increasing user comfort. Proactive prompts delivered with clear, supportive intentions and user-controlled engagement options were consistently preferred, indicating the importance of flexibility in smart speaker design.
        </p>
        <p className="mb-4">
          Overall, the GIDEA platform reinforced key insights into designing proactive voice assistants, emphasizing <strong>timely intervention</strong>, <strong>user control and autonomy</strong>, and careful consideration of <strong>contextual appropriateness</strong> to enhance user acceptance and interaction quality.
        </p>
      </div>
    ),
  },
  cs9: {
    simulationMedia: "Simulation/Media content for CS9 (placeholder)",
    userProfile: (
      <div>
        <div><strong>Subject ID:</strong> 1</div>
        <div><strong>Age:</strong> 22</div>
        <div><strong>Gender:</strong> Male</div>
        <div><strong>House Type:</strong> Family household</div>
        <div><strong>Study Time:</strong> Daytime</div>
        <div><strong>TIPI Scores:</strong>
          <ul className="list-disc list-inside ml-4">
            <li>Extraversion: 7</li>
            <li>Agreeableness: 3</li>
            <li>Conscientiousness: 1</li>
            <li>Emotional Stability: 1</li>
            <li>Openness: 7</li>
          </ul>
        </div>
        <div className="mt-2"><strong>Persona Description:</strong> Meet Alex, a vibrant 22-year-old living in a family household, thriving in the bustling energy of daytime activities. Bursting with ideas and a love for exploration, Alex enjoys engaging in creative projects and social gatherings, where his outgoing nature shines. He communicates with enthusiasm and originality, often sparking lively and spontaneous conversations. While Alex might sometimes seem disorganized or lose focus, his openness to new experiences makes him an adventurous spirit. He has a fondness for impromptu road trips but dislikes routine tasks, valuing freedom and novelty above all.</div>
      </div>
    ),
    detailedActivities: (
      <div>
        <div><strong>Time Stamp:</strong> 2025-02-06 2:30:00 pm</div>
        <div className="mt-2 whitespace-pre-line">
          <strong>Expanded Activity:</strong>
          <blockquote className="mt-1 pl-4 border-l-4 border-gray-300 text-gray-700">
            Alex takes a deep breath after finishing his yoga session, feeling a surge of energy and creativity ready to burst forth. He visualizes transforming the main room into his artistic haven for the afternoon. Dragging his fingers over his phone screen, he brushes away notifications, clearing his thoughts and refocusing on his next mission.

            With music softly emanating from his wireless speakers, he heads towards the shelf where his art supplies reside. Carefully, he opens the cabinet doors and grabs his collection of paints, brushes, and canvas, holding them securely against his chest as he walks back to the main table. Setting the items down gently, he’s mindful not to disturb the tranquility of the room. He positioning the materials just so, almost methodically, despite his usual tendency for disarray.

            Alex finds himself opening the nearby blinds wide, letting the afternoon sunlight pour in and energize the atmosphere. As he arranges his brushes in a makeshift holder, he contemplates his next masterpiece, ideas swirling like vivid colors in his mind. He reaches for a bottle of water, taking a sip to quench his thirst, feeling the coolness refresh him and propel his excitement even more.

            Glancing at the digital clock flashing on his stereo, he notices it's 2:45 pm, reminding him to pace his enjoyment and creativity to align with the ticking minutes. Each step in preparing feels deliberate and cherished, a warm-up for the creative exercise that Alex treasures deeply.
          </blockquote>
        </div>
      </div>
    ),
    conversation: cs9AssistantConversation,
    systemOverview: (
      <div>
        <h4 className="title is-6">Study Overview</h4>
        <p className="mb-4">
          Oh et al. (2024) investigated how <strong>proactive voice assistants (VAs)</strong> can support
          <strong> user autonomy</strong> in smart home environments through communication strategies that
          balance initiative with sensitivity. Using a <strong>Wizard-of-Oz approach</strong>, participants
          carried out daily home activities while a VA simulated by a human operator proactively asked if it was
          a good time to talk, based on movement detection.
        </p>
        <p className="mb-2"><strong>Key Findings:</strong></p>
        <ul className="list-disc list-inside ml-6 mb-4">
          <li>Participants responded more positively when the assistant initiated interaction during or after main activities.</li>
          <li>Timely and empathetic communication increased user receptivity to suggestions and follow-ups.</li>
          <li>Rejection of a suggestion was usually due to context, not disinterest in the assistant.</li>
          <li>Participants valued communication that acknowledged emotion, autonomy, and social awareness.</li>
        </ul>
      </div>
    ),
    gideaReplication: (
      <div>
        <h4 className="title is-6">
          Extended Findings with GIDEA
        </h4>
        <p className="mb-4">
          GIDEA replicated this interaction scenario by having avatars simulate smart home routines while the
          assistant agent adopted a proactive communication strategy, asking if it was a good time to engage
          when detecting activity changes.
        </p>
        <div className="flex flex-col md:flex-row gap-6 my-4 w-full justify-center items-center">
          <img src="/summary/cs9.png" alt="Original Study Illustration" className="w-full md:w-1/2 max-w-xl rounded shadow" />
          <img src="/summary/cs9_table.jpg" alt="GIDEA Replication Table" className="w-full md:w-1/2 max-w-3xl rounded shadow" />
        </div>
        <p className="mb-2"><strong>Paraphrased Key Results:</strong></p>
        <ul className="list-disc list-inside ml-6 mb-4">
          <li>The simulation closely matched the original study in <strong>timing and strategy use</strong> across activity types (pre-, main-, and post-activity phases).</li>
          <li>One notable difference: <strong>exploration strategies</strong> (e.g., asking follow-up questions about preferences) occurred more frequently during post-activity phases in GIDEA. This may reflect differences in operator behavior or avatar logic.</li>
          <li>Despite this, GIDEA accurately modeled <strong>context-sensitive interaction initiation</strong>, often engaging avatars during natural transitions.</li>
          <li>Simulated avatars adjusted their responsiveness based on timing, just as human participants did.</li>
          <li>Qualitative analysis showed avatars mirrored human reasoning in assessing the assistant's tone, empathy, and respect for autonomy.</li>
          <li>Both avatars and participants highlighted that <strong>declining a suggestion</strong> was a judgment of context, not an indicator of poor assistant quality.</li>
          <li>Socially aware behaviors—like small talk or emotional acknowledgment—were appreciated and led to greater avatar comfort.</li>
        </ul>
      </div>
    ),
  },
cs10: {
    simulationMedia: "Simulation/Media content for CS10 (placeholder)",
    userProfile: (
      <div>
        <div><strong>🆔 Subject ID:</strong> 1</div>
        <div><strong>🎂 Age:</strong> 24</div>
        <div><strong>👩 Gender:</strong> Female</div>
        <div><strong>🎓 Education:</strong> Degree in design, creatively oriented with minimal technology use</div>
        <div><strong>📱 Technology Usage:</strong> Rarely uses technology, curious but not committed, prefers face-to-face interactions</div>
        <br></br>
        <div><strong>💡 Personality at a Glance:</strong>
          <ul className="list-disc list-inside ml-4">
            <li>😄 Highly extraverted, vibrant and engaging (Extraversion: 7)</li>
            <li>🤝 Moderately agreeable, warm and friendly (Agreeableness: 5)</li>
            <li>📅 Low conscientiousness, spontaneous and flexible (Conscientiousness: 1)</li>
            <li>🌊 Moderately emotionally sensitive, creative and adaptive (Emotional Stability: 2)</li>
            <li>🎨 Open-minded, curious about new experiences (Openness: 6)</li>
          </ul>
        </div>
        <br></br>
        <div className="mt-2">
          <strong>📝 About Her:</strong> Samantha, a 24-year-old creative enthusiast with a degree in design, thrives in environments that spark her imagination. Though she rarely uses technology, she has dabbled with smart home and IFTTT applications, always curious but not committed. Her communication style is warm and engaging, preferring face-to-face interactions over digital ones. Known for her love of vibrant colors and minimalism, she dislikes clutter and adores discovering new cafes. Her spontaneous nature often surprises friends, bringing refreshing energy to every conversation.
        </div>
      </div>
    ),
    detailedActivities: (
      <div>
        <div><strong>🕰️ Time:</strong> Feb 6, 2025 – 12:30 PM to 12:35 PM</div>
        <div className="mt-2 whitespace-pre-line">
          <strong>📖 What's Happening:</strong>
          <blockquote className="mt-1 pl-4 border-l-4 border-gray-300 text-gray-700">
            Samantha settles into her cozy living room, colorful design sketches scattered around her. She glances at the minimalist light strip, contemplating the color transformation she's about to initiate. 'Let's infuse some vibrant energy here,' she muses, her heart warmed by the idea of vibrant colors enlivening her workspace. She approaches the light switch panel on the coffee table, her fingertips lightly grazing its surface. With a quick press, the ambient light strip springs to life, its radiant colors casting dynamic hues across the room. As the room fills with a kaleidoscope of vibrant colors, Samantha feels a rush of creativity. She nods approvingly, 'Perfect, just what I needed to get inspired.' Her inner artist feels invigorated, ready to dive into her latest project. She moves to her desk, her mind swirling with ideas, the colorful lights a constant source of motivation. She adjusts her position on the chair, getting comfortable as she starts sketching, the vibrant ambiance bolstering her imaginative journey.
          </blockquote>
        </div>
      </div>
    ),
    conversation: cs10AssistantConversation,
    systemOverview: (
      <div>
        <h4 className="title is-6">Study Overview</h4>
        <p className="mb-4">
          The study titled <em>"Exploring User Preferences for In-situ Programming of Smart Home Rules"</em> investigates user experiences and preferences in setting personalized smart home rules. The research specifically focuses on understanding how users conceptualize, implement, and interact with automated home environments through natural language instructions, exploring aspects such as <strong>usability</strong>, <strong>user comfort</strong>, and <strong>intuitive customization</strong>.
        </p>
        <p className="mb-4">
          Original findings highlight positive responses to smart home configurations enhancing comfort, creativity, and relaxation without causing undue complexity or cognitive load. Users particularly appreciate rules that blend seamlessly into daily routines, proactively supporting their tasks and moods.
        </p>
        <p className="mb-4">
          The study emphasizes the critical importance of understanding <strong>context</strong>, <strong>user autonomy</strong>, and the delicate balance required to provide proactive assistance without compromising user satisfaction and comfort.
        </p>
      </div>
    ),
    gideaReplication: (
      <div>
        <h4 className="title is-6">
          Extended Findings with GIDEA
        </h4>
        <p className="mb-4">
          Using the GIDEA platform, the study's findings on smart home preferences and in-situ programming were expanded and verified. Avatars consistently preferred proactive interactions that were perceived as highly <strong>useful</strong>, <strong>contextually appropriate</strong>, and respectful of <strong>user autonomy</strong>. These preferences particularly stood out in scenarios involving ambient lighting, personalized music, and comfortable temperature settings.
        </p>
        <div className="flex flex-col md:flex-row gap-6 my-4 w-full justify-center items-center">
          <img src="/summary/cs10.png" alt="Original Study Illustration" className="w-full md:w-1/3 max-w-lg rounded shadow" />
          <img src="/summary/cs10_table.jpg" alt="GIDEA Replication Table" className="w-full md:w-2/3 max-w-2xl rounded shadow" />
        </div>
        <p className="mb-4">
          The simulations emphasized the importance of customizable and intuitive interfaces, providing immediate and clear feedback to ensure accurate and satisfying user experiences. Users expressed significant dissatisfaction with poorly timed or intrusive interventions, reinforcing the critical need for <strong>context sensitivity</strong> and <strong>timing precision</strong>.
        </p>
        <p className="mb-4">
          Overall, the GIDEA platform reinforced key insights into designing intuitive smart home environments, highlighting the necessity of <strong>simplicity</strong>, <strong>user control</strong>, and thoughtful <strong>contextual customizations</strong>, thereby significantly enhancing user comfort, satisfaction, and seamless integration of smart technology into everyday life.
        </p>
      </div>
    ),
  },
};

export default caseStudyContents; 